// Advanced Analytics System for Service-Based Portfolio
class PortfolioAnalytics {
  constructor() {
    this.sessionId = this.generateSessionId();
    this.startTime = Date.now();
    this.events = [];
    this.initialized = false;
    
    // Initialize Google Analytics and custom tracking
    this.initializeTracking();
  }

  generateSessionId() {
    return 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }

  initializeTracking() {
    // Google Analytics 4 initialization (if gtag is available)
    if (typeof window.gtag === 'function') {
      this.initialized = true;
      console.log('📊 Analytics initialized with GA4');
    } else {
      // Fallback to custom analytics
      this.initializeCustomAnalytics();
    }
  }

  initializeCustomAnalytics() {
    // Custom analytics endpoint setup
    this.analyticsEndpoint = '/api/analytics.php';
    this.initialized = true;
    console.log('📊 Custom analytics initialized');
  }

  // Track service selection on welcome page
  trackServiceSelection(selectedServices, timeToSelect) {
    const event = {
      type: 'service_selection',
      sessionId: this.sessionId,
      timestamp: Date.now(),
      data: {
        selectedServices,
        serviceCount: selectedServices.length,
        timeToSelect, // time spent on welcome page before selection
        isDigitalMarketing: selectedServices.includes('digital-marketing'),
        isWebDevelopment: selectedServices.includes('web-development'),
        isBothServices: selectedServices.includes('digital-marketing') && selectedServices.includes('web-development')
      }
    };

    this.logEvent(event);
    
    // Google Analytics event
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'service_selection', {
        event_category: 'conversion',
        event_label: selectedServices.join(', '),
        value: selectedServices.length,
        custom_service_path: this.getServicePathName(selectedServices)
      });
    }
  }

  // Track section engagement
  trackSectionView(sectionName, selectedServices, timeSpent = 0) {
    const servicePath = this.getServicePathName(selectedServices);
    
    const event = {
      type: 'section_view',
      sessionId: this.sessionId,
      timestamp: Date.now(),
      data: {
        sectionName,
        servicePath,
        selectedServices,
        timeSpent,
        customizedContent: true
      }
    };

    this.logEvent(event);

    // Google Analytics event
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'page_view', {
        page_title: `${sectionName} - ${servicePath}`,
        page_location: `${window.location.origin}#${sectionName}`,
        custom_service_path: servicePath
      });
    }
  }

  // Track CTA clicks with service context
  trackCTAClick(ctaText, ctaType, selectedServices, section) {
    const servicePath = this.getServicePathName(selectedServices);
    
    const event = {
      type: 'cta_click',
      sessionId: this.sessionId,
      timestamp: Date.now(),
      data: {
        ctaText,
        ctaType, // 'primary', 'secondary', 'email', 'phone'
        section,
        servicePath,
        selectedServices
      }
    };

    this.logEvent(event);

    // Google Analytics conversion event
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'conversion', {
        event_category: 'cta_click',
        event_label: `${ctaText} - ${servicePath}`,
        value: ctaType === 'primary' ? 10 : 5, // Assign values to CTA types
        custom_service_path: servicePath,
        custom_section: section
      });
    }
  }

  // Track form interactions
  trackFormInteraction(formField, action, selectedServices, formData = {}) {
    const servicePath = this.getServicePathName(selectedServices);
    
    const event = {
      type: 'form_interaction',
      sessionId: this.sessionId,
      timestamp: Date.now(),
      data: {
        formField,
        action, // 'focus', 'fill', 'submit', 'abandon'
        servicePath,
        selectedServices,
        serviceSelected: formData.service || 'not_selected',
        formCompletionLevel: this.calculateFormCompletion(formData)
      }
    };

    this.logEvent(event);

    // Track form abandonment and completion rates
    if (action === 'submit') {
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'form_submit', {
          event_category: 'lead_generation',
          event_label: servicePath,
          value: 50, // High value for form submissions
          custom_service_path: servicePath,
          custom_service_requested: formData.service
        });
      }
    }
  }

  // Track pricing interest (for dynamic pricing feature)
  trackPricingView(serviceType, priceRange, selectedServices) {
    const servicePath = this.getServicePathName(selectedServices);
    
    const event = {
      type: 'pricing_view',
      sessionId: this.sessionId,
      timestamp: Date.now(),
      data: {
        serviceType,
        priceRange,
        servicePath,
        selectedServices
      }
    };

    this.logEvent(event);
  }

  // Calculate form completion percentage
  calculateFormCompletion(formData) {
    const requiredFields = ['name', 'email', 'service', 'message'];
    const filledFields = requiredFields.filter(field => formData[field] && formData[field].trim());
    return Math.round((filledFields.length / requiredFields.length) * 100);
  }

  // Get human-readable service path name
  getServicePathName(selectedServices) {
    if (!selectedServices || selectedServices.length === 0) {
      return 'unknown';
    }
    
    const isDigitalMarketing = selectedServices.includes('digital-marketing');
    const isWebDevelopment = selectedServices.includes('web-development');
    
    if (isDigitalMarketing && isWebDevelopment) {
      return 'full_stack_digital';
    } else if (isDigitalMarketing) {
      return 'digital_marketing';
    } else if (isWebDevelopment) {
      return 'web_development';
    }
    
    return 'other';
  }

  // Log event to console and send to backend
  logEvent(event) {
    this.events.push(event);
    console.log('📊 Analytics Event:', event);
    
    // Send to backend analytics endpoint
    this.sendEventToBackend(event);
  }

  // Send analytics data to backend
  async sendEventToBackend(event) {
    try {
      if (this.analyticsEndpoint) {
        await fetch(this.analyticsEndpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            ...event,
            userAgent: navigator.userAgent,
            referrer: document.referrer,
            viewport: {
              width: window.innerWidth,
              height: window.innerHeight
            },
            url: window.location.href
          })
        });
      }
    } catch (error) {
      console.log('📊 Analytics backend unavailable, storing locally');
      this.storeEventLocally(event);
    }
  }

  // Store event in localStorage as backup
  storeEventLocally(event) {
    try {
      const storedEvents = JSON.parse(localStorage.getItem('portfolio_analytics') || '[]');
      storedEvents.push(event);
      
      // Keep only last 100 events to avoid storage bloat
      if (storedEvents.length > 100) {
        storedEvents.splice(0, storedEvents.length - 100);
      }
      
      localStorage.setItem('portfolio_analytics', JSON.stringify(storedEvents));
    } catch (error) {
      console.log('📊 Unable to store analytics locally');
    }
  }

  // Get analytics summary for admin
  getAnalyticsSummary() {
    const summary = {
      sessionId: this.sessionId,
      sessionDuration: Date.now() - this.startTime,
      totalEvents: this.events.length,
      eventsByType: {},
      servicePathData: {}
    };

    // Group events by type
    this.events.forEach(event => {
      if (!summary.eventsByType[event.type]) {
        summary.eventsByType[event.type] = 0;
      }
      summary.eventsByType[event.type]++;

      // Track service path performance
      if (event.data && event.data.servicePath) {
        if (!summary.servicePathData[event.data.servicePath]) {
          summary.servicePathData[event.data.servicePath] = 0;
        }
        summary.servicePathData[event.data.servicePath]++;
      }
    });

    return summary;
  }

  // Export data for analysis
  exportAnalyticsData() {
    return {
      sessionInfo: {
        sessionId: this.sessionId,
        startTime: this.startTime,
        duration: Date.now() - this.startTime,
        userAgent: navigator.userAgent,
        referrer: document.referrer
      },
      events: this.events,
      summary: this.getAnalyticsSummary()
    };
  }
}

// Global analytics instance
const portfolioAnalytics = new PortfolioAnalytics();

export default portfolioAnalytics;
